import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { EmptyStatesDoqContainer } from '@ardoq/graph';
import { FlexBox, Stack } from '@ardoq/layout';
import { NoNumericFieldsDoq } from 'tabview/graphViews/emptyStates/NoNumericFieldsDoq';
import type { ChartEmptyReason } from './types';

/**
 * What a gridded chart says when its components share too few positions.
 *
 * Resolved by the view rather than keyed here, because the two gridded charts explain themselves
 * differently and `t()` needs its keys written out where they are used, not passed in as
 * variables - an interpolated key is invisible to the extractor.
 */
export type TooFewPositionsCopy = {
  title: string;
  explanation: string;
  remedy: string;
};

/** A title and one paragraph, for the reasons whose remedy is not the caller's to give. */
export type EmptyStateCopy = {
  title: string;
  message: string;
};

type TooFewPositionsDoqProps = { copy: TooFewPositionsCopy };

export const TooFewPositionsDoq = ({
  copy: { title, explanation, remedy },
}: TooFewPositionsDoqProps) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.EMPTY_BOX}
      title={title}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">{explanation}</FlexBox>
          <FlexBox gap="medium">{remedy}</FlexBox>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

export type ChartEmptyStateProps = {
  emptyReason: ChartEmptyReason;
  /**
   * Why the chart has too few positions to draw, worded for the chart asking.
   *
   * Required on all four. The gridded pair reach it when the components share fewer than three
   * positions; the point pair reach it when every component was excluded, which with a threshold
   * of one is the only way they can - and `countDistinctPositions` runs after exclusions, so it is
   * a state they hit rather than a theoretical one.
   */
  tooFewPositions: TooFewPositionsCopy;
  /** The state every one of these views opens in, since none of them preselects. */
  noFieldsSelected: EmptyStateCopy;
  noComponents: EmptyStateCopy;
};

/**
 * One message per reason, because each has a different remedy.
 *
 * A single "nothing to show" leaves a user changing fields at random when the real problem is
 * that the workspace has no numeric fields at all, or that every component sits on the same two
 * positions. Shared across the four charts, since three of the four reasons mean the same thing
 * whichever one is asking.
 */
const MessageDoq = ({
  copy: { title, message },
  doqType,
}: {
  copy: EmptyStateCopy;
  doqType: DoqType;
}) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={doqType}
      title={title}
      message={<FlexBox gap="medium">{message}</FlexBox>}
    />
  </EmptyStatesDoqContainer>
);

export const ChartEmptyState = ({
  emptyReason,
  noFieldsSelected,
  noComponents,
  tooFewPositions,
}: ChartEmptyStateProps) => {
  if (emptyReason === 'no numeric fields') {
    return <NoNumericFieldsDoq doqType={DoqType.BUBBLE_CHART_ERROR} />;
  }

  if (emptyReason === 'too few positions') {
    return <TooFewPositionsDoq copy={tooFewPositions} />;
  }

  /* Our own message rather than `NoDataAvailableDoq`, which was what this borrowed. An empty
     `scopeComponentIds` in viewpoint mode has three causes - the dataset is empty, a filter or a
     hidden loaded state removed everything, or a required path matched nothing - and that doq
     collapses all three into "add data through the Grid Editor", with an action button on it. For
     the filtered case that is the one remedy guaranteed not to work: the components exist. The
     studio's own `buildEmptyStateLayoutBasedOnDataset` tells the three apart and covers this one
     wherever it arrives, being absolutely positioned over an opaque surface. */
  if (emptyReason === 'no components') {
    return <MessageDoq copy={noComponents} doqType={DoqType.EMPTY_BOX} />;
  }

  /* No fields picked yet, which is how every one of these views opens - nothing is preselected,
     because guessing which numeric fields mean something produces a confident-looking chart out
     of two arbitrary columns. So this is the first thing every user sees rather than an edge case,
     which is why it does not borrow the timeline's `NoDateRangeTimeFieldsSelectedDoq`: that one
     says "all date range or time fields are currently hidden" and asks for a date or time field -
     wrong twice over on charts that take only numeric fields and are hiding nothing. */
  return (
    <MessageDoq copy={noFieldsSelected} doqType={DoqType.BUBBLE_CHART_ERROR} />
  );
};
